import { useEffect } from 'react';

import jwtDecode from 'jwt-decode';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  confirmWalletInvitation,
  getWalletInvitationData,
  isWalletInvitationEmailMatch,
  type WalletInvitationTokenData,
} from 'src/services/invite-monitor';
import { setBannerMessage } from 'src/store/display';

export const INVITE_MONITOR_EMAIL_KEY = 'walletInvitationData';
export const INVITE_MONITOR_TOKEN_KEY = 'walletInvitationToken';
export const INVITE_MONITOR_ACTIVE_JOURNEY_KEY =
  'walletInvitationActiveJourney';

type DecodedToken = {
  exp?: number;
};

export function getInviteState(): WalletInvitationTokenData | null {
  try {
    const storedState = localStorage.getItem(INVITE_MONITOR_EMAIL_KEY);

    return storedState ? JSON.parse(storedState) : null;
  } catch {
    return null;
  }
}

export function getInviteToken(): string {
  return localStorage.getItem(INVITE_MONITOR_TOKEN_KEY) || '';
}

export function setInviteState(data: WalletInvitationTokenData): void {
  localStorage.setItem(INVITE_MONITOR_EMAIL_KEY, JSON.stringify(data));
}

export function setInviteToken(token: string): void {
  localStorage.setItem(INVITE_MONITOR_TOKEN_KEY, token);
}

export function hasActiveInviteJourney(): boolean {
  try {
    return sessionStorage.getItem(INVITE_MONITOR_ACTIVE_JOURNEY_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setActiveInviteJourney(): void {
  sessionStorage.setItem(INVITE_MONITOR_ACTIVE_JOURNEY_KEY, 'true');
}

export function clearInviteState(): void {
  localStorage.removeItem(INVITE_MONITOR_TOKEN_KEY);
  localStorage.removeItem(INVITE_MONITOR_EMAIL_KEY);
  sessionStorage.removeItem(INVITE_MONITOR_ACTIVE_JOURNEY_KEY);
}

export function isWalletInvitationFlowPath(pathname: string): boolean {
  return /^\/(cadastro|perfil-social)(\/|$)/.test(pathname);
}

export function shouldInvalidatePersistedInviteState(
  pathname: string,
  search: string,
): boolean {
  if (!isWalletInvitationFlowPath(pathname)) {
    return false;
  }

  const searchParams = new URLSearchParams(search);
  const tokenParam = searchParams.get('t') ?? '';

  if (tokenParam) {
    return false;
  }

  return !!(getInviteToken() || getInviteState());
}

function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    if (typeof decoded.exp === 'number') {
      return decoded.exp < Date.now() / 1000;
    }
    return false;
  } catch {
    try {
      const payloadPart = token.split('.')[0];
      const decoded = JSON.parse(atob(payloadPart));
      if (typeof decoded.expires === 'number') {
        return decoded.expires < Date.now();
      }
      return false;
    } catch {
      return false;
    }
  }
}

type UseWalletInvitationOptions = {
  onConfirmSuccess?: (data: WalletInvitationTokenData) => void;
  onConfirmError?: (error: unknown) => void;
};

export function useWalletInvitation(options?: UseWalletInvitationOptions) {
  const { auth } = useAuthContext();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tokenParam = searchParams.get('t') ?? '';
  const dispatch = useDispatch();

  if (tokenParam) {
    setInviteToken(tokenParam);
    setActiveInviteJourney();
  }

  const shouldInvalidateStoredInviteState =
    shouldInvalidatePersistedInviteState(location.pathname, location.search);

  if (shouldInvalidateStoredInviteState) {
    clearInviteState();
  }

  const storedToken = shouldInvalidateStoredInviteState ? '' : getInviteToken();
  const inviteState = shouldInvalidateStoredInviteState
    ? null
    : getInviteState();

  const resolvedToken = tokenParam || storedToken;
  const isExpired = isTokenExpired(resolvedToken);

  if (isExpired) {
    clearInviteState();
  }

  const token = isExpired ? '' : resolvedToken;
  const walletInvitationData = isExpired ? null : inviteState;

  const isInsideInvitationFlow = isWalletInvitationFlowPath(location.pathname);

  useEffect(() => {
    if (isInsideInvitationFlow) {
      return;
    }

    if (!inviteState && !storedToken) {
      return;
    }

    clearInviteState();
  }, [inviteState, isInsideInvitationFlow, storedToken]);

  const { data: invitationData, isLoading: isLoadingWalletInvitation } =
    useQuery({
      queryKey: ['walletInvitationData', token],
      queryFn: () => getWalletInvitationData(token),
      enabled: !!token,
      retry: false,
      onError: (_: any) => {
        dispatch(
          setBannerMessage({
            message: 'Erro ao buscar dados do convite',
            type: 'error',
            autoclose: true,
          }),
        );
      },
      onSuccess: (data) => {
        if (data) {
          setInviteState(data);
          return;
        }

        clearInviteState();
      },
    });

  const invitationEmail = invitationData?.email ?? walletInvitationData?.email;
  const authenticatedEmail = auth.user?.email;
  const canConfirmWalletInvitation = isWalletInvitationEmailMatch(
    invitationEmail,
    authenticatedEmail,
  );
  const hasInvitationEmailMismatch =
    !!authenticatedEmail && !!invitationEmail && !canConfirmWalletInvitation;

  const confirmWalletInvitationMutation = useMutation(confirmWalletInvitation, {
    onError: (error: any) => {
      dispatch(
        setBannerMessage({
          message: 'Erro ao confirmar convite. Tente novamente.',
          type: 'error',
          autoclose: true,
        }),
      );
      options?.onConfirmError?.(error);
    },
    onSuccess: (data) => {
      clearInviteState();

      if (options?.onConfirmSuccess) {
        options.onConfirmSuccess(data);
      } else {
        window.location.href = `/monitor/${data.owner.id}?tab=accepted`;
      }
    },
  });

  const confirm = (confirmationToken: string) => {
    if (!canConfirmWalletInvitation) {
      return;
    }

    confirmWalletInvitationMutation.mutate(confirmationToken);
  };

  return {
    isLoading: isLoadingWalletInvitation,
    token,
    data: invitationData ?? walletInvitationData,
    invitationEmail,
    authenticatedEmail,
    confirm,
    canConfirmWalletInvitation,
    hasInvitationEmailMismatch,
    isLoadingConfirm: confirmWalletInvitationMutation.isLoading,
    clearInviteState,
  };
}
